import { Box, Button, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilters, TOrderBy } from 'redux/types';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import {
	getPpwrPackagingTypeOptionsRequest,
	searchPpwrPackagingTypesRequest,
} from 'redux/reducers/ppwr/reducer';
import {
	getPpwrPackagingTypeOptions,
	getPpwrPackagingTypes,
	getPpwrPackagingTypesLoading,
} from 'redux/reducers/ppwr/selectors';
import { TPpwrPackagingType } from 'redux/reducers/ppwr/types';
import {
	DEFAULT_PACKAGING_TYPE_SELECTOR,
	tableStructure as defaultTableStructure,
} from './tableStructure';
import { PackagingTypeDialog } from './PackagingTypeDialog';

const EDIT_ACTION = 'Edit';

export const PackagingTypes: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [packagingTypeToEdit, setPackagingTypeToEdit] = useState<TPpwrPackagingType | null>(null);

	const list = useSelector(getPpwrPackagingTypes);
	const listLoading = useSelector(getPpwrPackagingTypesLoading);
	const options = useSelector(getPpwrPackagingTypeOptions);

	/** The dialog picks the default packaging type out of this list, and the table resolves its name from it. */
	useEffect(() => {
		if (!options) dispatch(getPpwrPackagingTypeOptionsRequest());
	}, [options, dispatch]);

	const structure = useMemo(
		() =>
			currentTableStructure.map((column) =>
				column.selector === DEFAULT_PACKAGING_TYPE_SELECTOR
					? {
							...column,
							value: (item: TPpwrPackagingType) =>
								item.defaultPckgTypeId === null
									? ''
									: (options?.find((option) => option.id === item.defaultPckgTypeId)
											?.pckgTypeName ?? String(item.defaultPckgTypeId)),
						}
					: column,
			),
		[currentTableStructure, options],
	);

	/** Keeps only the visibility flags, so the columns stay free of the derived render callbacks. */
	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TPpwrPackagingType>[]) => {
			setCurrentTableStructure((previous) =>
				previous.map((column) => {
					const changed = newTableStructure.find((item) => item.selector === column.selector);
					return changed ? { ...column, show: changed.show } : column;
				}),
			);
		},
		[],
	);

	const loadList = useCallback(() => {
		const params = queryString.parse(search);
		const filters = getFiltersFromNavigateParams(currentTableStructure, params) as TFilters;

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: defaultTableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.DESC });

		dispatch(
			searchPpwrPackagingTypesRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(params?.search ?? ''),
				filters,
				orderBy,
			}),
		);
	}, [currentTableStructure, dispatch, search]);

	useEffect(() => {
		loadList();
	}, [loadList]);

	const handleAddNew = useCallback(() => {
		setPackagingTypeToEdit(null);
		setDialogOpen(true);
	}, []);

	const handleAction = useCallback(
		(id: number, action: string) => {
			if (action === EDIT_ACTION) {
				setPackagingTypeToEdit(list?.data?.find((item) => item.id === id) ?? null);
				setDialogOpen(true);
			}
		},
		[list],
	);

	const handleDialogClose = useCallback(() => {
		setDialogOpen(false);
		setPackagingTypeToEdit(null);
	}, []);

	return (
		<>
			<PackagingTypeDialog
				open={dialogOpen}
				onClose={handleDialogClose}
				packagingTypeToEdit={packagingTypeToEdit}
			/>
			<EasTableWithFilter<TPpwrPackagingType>
				tableStructure={structure}
				items={list}
				itemsIsLoading={listLoading}
				handleAction={handleAction}
				onTableStructureChange={handleTableStructureChange}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary">
						Resources / Reference Tables / PPWR / <b>Packaging Types</b>
					</Typography>
					<Button variant="contained" color="primary" size="small" onClick={handleAddNew}>
						Add New Packaging Type
					</Button>
				</Box>
			</EasTableWithFilter>
		</>
	);
};
